import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { CarrouselFacultadesComponent } from '../carrousel-facultades/carrousel-facultades.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-facultad-oferta-academica',
  standalone: true,
  imports: [CommonModule, CarrouselFacultadesComponent],
  templateUrl: './facultad-oferta-academica.component.html',
  styleUrl: './facultad-oferta-academica.component.scss'
})
export class FacultadOfertaAcademicaComponent implements AfterViewInit, OnDestroy {
  @Input() info:any;
  @ViewChildren('description') descriptions!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('blockTitle') blockTitles!: QueryList<ElementRef<HTMLElement>>;

  private descriptionObserver?: ResizeObserver;
  private descriptionsSubscription?: Subscription;

  ngAfterViewInit(): void {
    this.descriptionsSubscription = this.descriptions.changes.subscribe(() => this.observeDescriptions());
    this.observeDescriptions();
  }

  ngOnDestroy(): void {
    this.descriptionObserver?.disconnect();
    this.descriptionsSubscription?.unsubscribe();
  }

  private observeDescriptions(): void {
    this.descriptionObserver?.disconnect();

    if (typeof ResizeObserver === 'undefined') return;

    this.descriptionObserver = new ResizeObserver(() => {
      this.updateTitleHeights();
      this.updateDescriptionLines();
    });
    const section = this.blockTitles.first?.nativeElement.closest('.seccion-posgrados');
    if (section) this.descriptionObserver.observe(section);
    this.descriptions.forEach(description => {
      const info = description.nativeElement.closest('.info-bloque');
      if (info) this.descriptionObserver?.observe(info);
    });

    this.updateTitleHeights();
    this.updateDescriptionLines();
  }

  private updateTitleHeights(): void {
    const titles = this.blockTitles?.toArray().map(title => title.nativeElement) || [];
    if (!titles.length) return;

    titles.forEach(title => {
      title.style.height = 'auto';
      title.style.minHeight = '0';
    });

    const maxHeight = Math.max(...titles.map(title => title.getBoundingClientRect().height));
    titles.forEach(title => {
      title.style.height = `${Math.ceil(maxHeight)}px`;
      title.style.minHeight = `${Math.ceil(maxHeight)}px`;
    });
  }

  private updateDescriptionLines(): void {
    this.descriptions.forEach(description => {
      const element = description.nativeElement;
      const info = element.closest('.info-bloque') as HTMLElement | null;
      if (!info || !info.clientHeight) return;

      const action = info.querySelector('.btn-posgrado, .contenedor-botones-iconos') as HTMLElement | null;
      const actionStyle = action ? getComputedStyle(action) : null;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 16;
      const actionHeight = action ? action.offsetHeight + parseFloat(actionStyle?.marginTop || '0') : 0;
      const availableHeight = Math.max(lineHeight, info.clientHeight - actionHeight);
      const lines = Math.max(1, Math.floor(availableHeight / lineHeight));
      const textHeight = lines * lineHeight;

      const fullText = element.dataset['fullText'] || element.textContent?.trim() || '';
      element.dataset['fullText'] = fullText;
      element.style.display = 'block';
      element.style.height = `${textHeight}px`;
      element.style.maxHeight = `${textHeight}px`;
      element.style.setProperty('-webkit-line-clamp', 'unset');
      element.style.setProperty('line-clamp', 'unset');
      element.textContent = fullText;

      if (element.scrollHeight <= element.clientHeight + 1) return;

      let low = 0;
      let high = fullText.length;
      let best = '...';

      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const candidate = `${fullText.slice(0, middle).replace(/\s+$/, '')}...`;
        element.textContent = candidate;

        if (element.scrollHeight <= element.clientHeight + 1) {
          best = candidate;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      const lastSpace = best.lastIndexOf(' ');
      element.textContent = lastSpace > 0
        ? `${best.slice(0, lastSpace).replace(/\s+$/, '')}...`
        : best;
    });
  }
}
